import { Alert, Snackbar } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EnhancedTable from "../../../Components/EnhancedTable";
import ExpenseHeader from "../../../Components/ExpenseHeader";
import PayableSummary from "../../../Components/PaymentSummary";
import useAuthStore from "../../../store/useAuthStore";

const headCells = [
  { id: "header", numeric: true, disablePadding: true, label: "Expense Header" },
  { id: "subheader", numeric: true, disablePadding: true, label: "Expense Subheader" },
  { id: "amount", numeric: true, disablePadding: false, label: "Amount (Nu)" },
  { id: "description", numeric: false, disablePadding: false, label: "Description" },
  { id: "bankName", numeric: false, disablePadding: false, label: "Bank" },
  { id: "paymentMethod", numeric: false, disablePadding: false, label: "Payment Method" },
  { id: "date", numeric: true, disablePadding: false, label: "Date" },
];

const Expense = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateRange, setDateRange] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const hasLoadedOnce = useRef(false);

  const fetchExpenses = React.useCallback(async () => {
    setLoading(true);
    try {
      const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      };

      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        startDate: startDate ? formatDate(startDate) : "",
        endDate: endDate ? formatDate(endDate) : "",
      });

      const response = await fetch(`/api/getAllExpense?${queryParams}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch expenses");

      const data = await response.json();
      setRows(
        data.expenses.map((expense) => ({
          id: expense._id,
          header: expense.header,
          subheader: expense.taxRate ? `${expense.subheader} (${expense.taxRate}%)` : expense.subheader,
          amount: expense.amount,
          description: expense.description,
          bankName: expense.bankName || "-",
          paymentMethod: expense.paymentMethod || "-",
          date: expense.date,
        }))
      );
      setTotalCount(data.totalCount || 0);
      hasLoadedOnce.current = true;
    } catch (error) {
      console.error("Error fetching expenses: ", error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleDeleteExpenses = async (selectedIds) => {
    try {
      const response = await fetch("/api/deleteExpense", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete expenses");
      fetchExpenses();
      setSnackbar({ open: true, message: "Expenses deleted successfully!", severity: "success" });
    } catch (error) {
      console.error("Error deleting expenses:", error);
      setSnackbar({ open: true, message: error.message || "Failed to delete expenses", severity: "error" });
    }
  };

  const handleEdit = (row) => navigate("/editExpense", { state: { row } });
  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (_event, newRowsPerPage) => { setRowsPerPage(newRowsPerPage); setPage(0); };

  return (
    <div>
      <ExpenseHeader className="mb-4" />

      <PayableSummary
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        dateRange={dateRange}
        setDateRange={setDateRange}
        visibleRows={rows}
        headCells={headCells}
      />

      {/* Table area — skeleton on first load, overlay spinner on refetch */}
      {!hasLoadedOnce.current && loading ? (
        <div className="animate-pulse space-y-3 mt-2">
          <div className="bg-gray-200 h-10 rounded-lg w-full" />
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-gray-100 h-8 rounded w-full" />
          ))}
        </div>
      ) : (
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg">
              <div className="flex items-center gap-2 text-blue-600">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <span className="text-sm font-medium">Loading...</span>
              </div>
            </div>
          )}
          <EnhancedTable
            rows={rows}
            headCells={headCells}
            onDelete={handleDeleteExpenses}
            onEdit={handleEdit}
            serverSide={true}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            color: "white",
            backgroundColor: snackbar.severity === "success" ? "#22c55e" : "#ef4444",
            "& .MuiAlert-icon": { color: "white" },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Expense;
